import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { cn } from 'helpers/classnames';
import ProductCard from 'components/common/ProductCard';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { evRecommendationClick, evRecommendationImpressionWrapper } from 'events/recommendations';
import { trackEvent } from 'helpers/analytics';
import { track } from 'apis/amethyst';
import useMartyContext from 'hooks/useMartyContext';
import ColorSwatchWrapper from 'components/search/ColorSwatches';
import { evProductInteract } from 'events/search';
import { getProductWithRelatedStyles } from 'helpers/RecoUtils';
import { createModuleViewEvent, mapProductsWithRelationsFromCalypso, MODULE_EVENT_NAME } from 'actions/pixelServerUtil';
import { firePixelServer } from 'actions/pixelServer';
import { fireAmplitudeModuleClickedEvent } from 'utils/amplitude';
import { AMPLITUDE_MODULE_CLICKED, AMPLITUDE_PRODUCT_CLICKED_EVENT_ID, GTM_EVENT_KEY } from 'constants/amplitude';
import ProductUtils, { getProductGender, priceToFloat } from 'helpers/ProductUtils';

import css from 'styles/components/search/inlineRecos.scss';

const DISPLAYED_RECOS_LIMIT = 4;

export const SearchInlineRecos = ({
  inlineRecos = {},
  heartsInfo,
  msaImageParams,
  searchTerm,
  CardBottomSlot = null,
  titleClassName,
  sessionId
}) => {
  const { recos, title } = inlineRecos;
  const recoSource = 'EP13N';
  const { testId } = useMartyContext();
  const recoProductRelations = useSelector(state => state.products.recoProductRelations);
  const dispatch = useDispatch();

  const handleInlineRecoClick = useCallback(
    ({
      index,
      styleId,
      productId,
      brandName,
      productName,
      originalPrice,
      price,
      productType,
      txAttrFacet_Gender: genders,
      relatedStyles,
      defaultCatergory = ''
    }) => {
      trackEvent('TE_SEARCH_INLINE_RECO_CLICK', `styleId:${styleId}productIndex:${index}:searchTerm:${searchTerm}`);
      track(() => [
        evRecommendationClick,
        {
          index: index + 1,
          recommendationType: 'PRODUCT_RECOMMENDATION',
          recommendationValue: productId,
          recommendationSource: recoSource,
          widgetType: 'SIMILAR_PRODUCT_WIDGET',
          sourcePage: 'SEARCH_PAGE'
        }
      ]);

      const selectedStyle = relatedStyles.find(style => style.styleId === styleId);
      const isOnSale = ProductUtils.isStyleOnSale(selectedStyle);
      const gender = getProductGender(genders);

      const product = {
        brand: brandName,
        category: productType,
        gender,
        list_price: priceToFloat(originalPrice),
        name: productName,
        on_sale: isOnSale,
        price,
        sku: productId,
        styleId,
        subCategory: defaultCatergory,
        subcategory2: ''
      };

      fireAmplitudeModuleClickedEvent({
        event: AMPLITUDE_MODULE_CLICKED,
        sessionId,
        module: {
          type: 'product',
          name: title
        },
        product,
        [GTM_EVENT_KEY]: AMPLITUDE_PRODUCT_CLICKED_EVENT_ID
      });
    },
    [searchTerm]
  );

  useEffect(() => {
    const styleIds = recos.map(reco => reco.styleId);
    trackEvent('TE_SEARCH_INLINE_RECOS_VIEW', `styles:${styleIds}:searchTerm:${searchTerm}`);
    const recommendationImpression = [
      {
        numberOfRecommendations: styleIds.length,
        recommendationType: 'PRODUCT_RECOMMENDATION',
        recommendationSource: recoSource,
        widgetType: 'SIMILAR_PRODUCT_WIDGET'
      }
    ];

    track(() => [evRecommendationImpressionWrapper, { recommendationImpression }]);
  }, [recos, searchTerm]);

  useEffect(() => {
    if (recos?.length === 0 || recoProductRelations?.length === 0) {
      return;
    }

    const mappedRecos = recos.reduce((acc, reco, index) => {
      if (index < DISPLAYED_RECOS_LIMIT) {
        const { styleId } = reco;
        const productWithRelatedStyles = getProductWithRelatedStyles(styleId, recoProductRelations);
        if (productWithRelatedStyles) {
          acc.push(productWithRelatedStyles);
        }
      }
      return acc;
    }, []);

    const moduleViewEvent = createModuleViewEvent({
      moduleName: title ?? '',
      products: mapProductsWithRelationsFromCalypso({ products: mappedRecos })
    });
    dispatch(firePixelServer(MODULE_EVENT_NAME, moduleViewEvent));
  }, [recoProductRelations, recos]);

  return (
    recos?.length && (
      <>
        {title && <h2 className={cn(titleClassName, 'zappos:heading-s')}>{title}</h2>}
        <div className={css.recos} data-source={recoSource} data-test-id={'inlineRecos'}>
          {recos.map((reco, index) => {
            if (index < DISPLAYED_RECOS_LIMIT) {
              const { styleId, productId, link, productUrl } = reco;
              const url = link || productUrl; // if cabt wins won't need to explicitly send this; handled by ProductCard.jsx
              const productWithRelatedStyles = getProductWithRelatedStyles(styleId, recoProductRelations);
              const { relatedStyles, ...productInfo } = productWithRelatedStyles;

              if (relatedStyles?.length) {
                const onProductMediaHovered = (product, mainProductStyleId) => {
                  track(() => [
                    evProductInteract,
                    {
                      mainStyleId: mainProductStyleId,
                      interactedProduct: product,
                      interactionType: 'HOVER'
                    }
                  ]);
                };

                return (
                  <ColorSwatchWrapper
                    {...productInfo}
                    index={index}
                    relatedStyles={relatedStyles}
                    heartsInfo={heartsInfo}
                    key={`${productInfo.styleId}-${productInfo.colorId}`}
                    testId={testId('searchReco')}
                    msaImageParams={msaImageParams}
                    onClick={() =>
                      handleInlineRecoClick({
                        index,
                        styleId,
                        productId,
                        brandName: productInfo.brandName,
                        productName: productInfo.productName,
                        originalPrice: productInfo.originalPrice,
                        price: productInfo.price,
                        productType: productInfo.productType,
                        txAttrFacet_Gender: productInfo.txAttrFacet_Gender,
                        relatedStyles
                      })
                    }
                    onProductMediaHovered={onProductMediaHovered}
                    // reco exclusives
                    className={cn(css.productCardOverride)}
                    productUrl={url}
                    badges={productInfo.badges}
                    imageClassName={reco.rowHasBadge && css.rowHasBadge}
                  />
                );
              }

              const recoOtherProps = { ...reco, productType: productInfo.productType, txAttrFacet_Gender: productInfo.txAttrFacet_Gender };

              return (
                <ProductCard
                  {...recoOtherProps}
                  index={index}
                  hearts={heartsInfo}
                  msaImageParams={msaImageParams}
                  key={styleId + productId}
                  className={css.productCardOverride}
                  imageNoBackground={false}
                  productUrl={url}
                  onClick={() =>
                    handleInlineRecoClick({
                      index,
                      styleId,
                      productId,
                      brandName: productInfo.brandName,
                      productName: productInfo.productName,
                      originalPrice: productInfo.originalPrice,
                      price: productInfo.price,
                      productType: productInfo.productType,
                      txAttrFacet_Gender: productInfo.txAttrFacet_Gender,
                      relatedStyles
                    })
                  }
                  data-test-id={testId('searchReco')}
                  CardBottomSlot={CardBottomSlot}
                />
              );
            }
            return null;
          })}
        </div>
      </>
    )
  );
};

export default withErrorBoundary('SearchInlineRecos', SearchInlineRecos);
